<template>
  <div class="min-w-[1080px] bg-[#F6F6F6] dark:bg-[#181818]">
    <div
      class="fixed inset-0 z-1 hidden bg-cover bg-center bg-no-repeat opacity-10 dark:block"
      style="background-image: url(&quot;/indexbg.png&quot;)"
    ></div>
    <el-container>
      <el-header
        height="80px"
        class="fixed top-0 left-0 z-50 w-full bg-white/50 backdrop-blur-sm dark:bg-black/50"
      >
        <div class="flex items-center justify-between px-[35px] py-[20px]">
          <el-image
            class="h-[40px] w-[120px]"
            :src="isDark ? '/logo.png' : '/logo1.png'"
          />
          <el-input
            class="search-input basis-[300px] transition-all duration-300 hover:basis-[500px]"
            v-model="searchQuery"
            :placeholder="$t('search.placeholder')"
            :suffix-icon="Search"
          />
          <div class="flex items-center gap-2">
            <!-- <ThemeSwitch class="w-[50px] h-[20px]" /> -->

            <el-switch
              :active-action-icon="Sunny"
              :inactive-action-icon="Moon"
              v-model="isDark"
              @change="handleThemeChange"
            />
            <!-- <el-select v-model="locale" size="small" class="w-[100px]" :teleported="false">
              <el-option v-for="locale in locales" :key="locale" :label="locale.name" :value="locale.name" @select="setLocale(locale.code)" />
            </el-select> -->
            <template v-if="user">
              <el-dropdown @command="handleUserMenu">
                <span class="el-dropdown-link">
                  <el-avatar
                    :src="user.avatar || '/common/avatar.png'"
                    :size="40"
                  />
                </span>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="profile"
                      >个人中心</el-dropdown-item
                    >
                    <el-dropdown-item command="points"
                      >积分商城</el-dropdown-item
                    >
                    <el-dropdown-item command="history"
                      >观看历史</el-dropdown-item
                    >
                    <el-dropdown-item command="settings"
                      >账号设置</el-dropdown-item
                    >
                    <el-dropdown-item divided command="logout"
                      >退出</el-dropdown-item
                    >
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
            <template v-else>
              <el-button type="primary" @click="isLoginOpen = true">
                {{ $t("nav.login") }}
              </el-button>
            </template>
          </div>
        </div>
      </el-header>
      <el-container class="relative z-2">
        <el-aside
          width="280"
          class="fixed top-[80px] left-0 text-gray-800 dark:text-white"
        >
          <div>
            <div @click="handleNavigate('/')" :class="getNavClass('/')">
              <el-icon>
                <House />
              </el-icon>
              <span>{{ $t("nav.home") }}</span>
            </div>
            <div
              @click="handleNavigate('/schedule')"
              :class="getNavClass('/schedule')"
            >
              <el-icon>
                <Calendar />
              </el-icon>
              <span>{{ $t("nav.schedule") }}</span>
            </div>
            <div
              @click="handleNavigate('/broadcast')"
              :class="getNavClass('/broadcast')"
            >
              <el-icon>
                <VideoPlay />
              </el-icon>
              <span>{{ $t("nav.broadcast") }}</span>
            </div>
            <div
              @click="handleNavigate('/replay')"
              :class="getNavClass('/replay')"
            >
              <el-icon>
                <VideoCameraFilled />
              </el-icon>
              <span>{{ $t("nav.replay") }}</span>
            </div>
            <div
              @click="handleNavigate('/activities')"
              :class="getNavClass('/activities')"
            >
              <el-icon>
                <Trophy />
              </el-icon>
              <span>{{ $t("nav.activities") }}</span>
            </div>
            <div
              @click="handleNavigate('/download')"
              :class="getNavClass('/download')"
            >
              <el-icon>
                <Download />
              </el-icon>
              <span>{{ $t("nav.download") }}</span>
            </div>
          </div>
          <DownloadCard v-if="isActive('/')" />
        </el-aside>

        <el-main class="z-2 mt-[80px] ml-[120px]">
          <slot></slot>
        </el-main>
      </el-container>
      <el-footer height="auto" class="z-1 bg-[#16181A]">
        <div class="mx-auto py-[50px] text-center text-white">
          <div>{{ $t("footer.disclaimer.line1") }}</div>
          <div>{{ $t("footer.disclaimer.line2") }}</div>
          <div>{{ $t("footer.disclaimer.line3") }}</div>
          <div>{{ $t("footer.disclaimer.line4") }}</div>
          <div>Version: 6.1.4（11）</div>
        </div>
      </el-footer>
    </el-container>
  </div>

  <!-- 右侧悬浮按钮 - 固定定位在根容器 -->
  <FloatingActionButtons
    @chat-click="handleChatClick"
    @headset-click="handleHeadsetClick"
    @upload-click="handleUploadClick"
  />
  <LoginDialog v-model="isLoginOpen" />
</template>

<script setup lang="ts">
import {
  Search,
  House,
  Calendar,
  VideoPlay,
  VideoCameraFilled,
  Trophy,
  Download,
  Sunny,
  Moon,
} from "@element-plus/icons-vue";
const { locales, setLocale } = useI18n();

const route = useRoute();
const router = useRouter();
const searchQuery = ref("");
const { isDark, setDark, setLight } = useTheme();
const isLoginOpen = ref(false);
const user = useState("user", () => null as any);
const authCookie = useCookie<string | null>("auth_token", {
  default: () => null,
});

// 判断路由是否激活
const isActive = (path: string) => {
  if (path === "/") {
    return route.path === "/" || route.path === "";
  }
  return route.path.startsWith(path);
};

// 获取导航链接的样式类
const getNavClass = (path: string) => {
  return isActive(path)
    ? "flex px-[48px] items-center gap-2 p-3 cursor-pointer text-[#22C55E]"
    : "flex px-[48px] items-center gap-2 p-3 cursor-pointer text-[#94A3B8]";
};

// 处理导航点击
const handleNavigate = (path: string) => {
  if (path === "/broadcast" && !user.value) {
    isLoginOpen.value = true;
    return;
  }
  if (route.path !== path) {
    router.push(path);
  }
};

// 悬浮按钮事件处理
const handleChatClick = () => {
  console.log("点击聊天按钮");
  // 可以在这里打开聊天面板
};

const handleHeadsetClick = () => {
  console.log("点击耳机按钮");
  // 可以在这里处理音频相关功能
};

const handleUploadClick = () => {
  console.log("点击上传按钮");
  // 可以在这里处理上传功能
};

const handleThemeChange = (val: any) => {
  if (val) setDark();
  else setLight();
};

const handleUserMenu = async (cmd: string) => {
  if (cmd === "logout") {
    authCookie.value = null;
    user.value = null;
    ElMessage.success("已退出");
    router.push({ path: "/broadcast", query: { tab: "live" } });
    return;
  }
  const map: Record<string, string> = {
    profile: "profile",
    points: "points",
    history: "watch",
    settings: "settings",
  };
  const tab = map[cmd] || "live";
  router.push({ path: "/broadcast", query: { tab } });
};
</script>
<style scoped>
/* 搜索框样式 - 深色圆角样式 */
:deep(.search-input) {
  .el-input__wrapper {
    background-color: rgba(0, 0, 0, 0.04);
    border-radius: 9999px;
    box-shadow: none;
    padding: 8px 16px;
    transition: all 0.3s ease;
  }

  .el-input__wrapper:hover {
    background-color: rgba(0, 0, 0, 0.06);
    box-shadow: none;
  }

  .el-input__wrapper.is-focus {
    background-color: rgba(0, 0, 0, 0.06);
    box-shadow: none;
  }

  .el-input__inner {
    color: #333;
    background: transparent;
  }

  .el-input__inner::placeholder {
    color: #9ca3af;
  }

  .el-input__suffix {
    color: #6b7280;
  }

  .el-input__suffix-inner {
    pointer-events: all;
  }
}

:deep(html.dark) .search-input {
  .el-input__wrapper {
    background-color: rgba(255, 255, 255, 0.1);
  }
  .el-input__wrapper:hover,
  .el-input__wrapper.is-focus {
    background-color: rgba(255, 255, 255, 0.15);
  }
  .el-input__inner {
    color: rgba(255, 255, 255, 0.9);
  }
  .el-input__inner::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  .el-input__suffix {
    color: rgba(255, 255, 255, 0.8);
  }
}

.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
.tw-reset { color: inherit; }
